import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../api/client';
import { API_BASE_URL } from '../config';
import { toast } from 'sonner';
import { Settings, Key, Save, Eye, EyeOff } from 'lucide-react';

export function SettingsPage() {
  const { token } = useAuth();
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadSettings();
  }, [token]);

  const loadSettings = async () => {
    try {
      const data = await apiClient.getSettings(token);
      setSettings(data);
    } catch (error) {
      console.error('加载设置失败:', error);
      toast.error('加载设置失败');
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('请填写所有密码字段');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('新密码与确认密码不匹配');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('密码长度至少为6位');
      return;
    }

    setLoading(true);
    try {
      // 验证当前密码
      const loginResponse = await fetch(`${API_BASE_URL}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password: currentPassword }),
      });

      if (!loginResponse.ok) {
        toast.error('当前密码不正确');
        return;
      }

      // 更新密码
      await apiClient.updateSetting(token, {
        key: 'auth_password',
        value: newPassword,
      });

      toast.success('密码修改成功');
      
      // 清空表单
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPassword(false);
      
      // 重新加载设置
      await loadSettings();
    } catch (error) {
      console.error('修改密码失败:', error);
      toast.error('修改密码失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl p-6">
      <div className="glass-panel rounded-3xl p-8 text-white">
        <div className="mb-8 flex items-center gap-3">
          <Settings className="h-8 w-8 text-blue-400" />
          <h1 className="text-3xl font-bold">系统设置</h1>
        </div>

        <div className="space-y-8">
          {/* 密码修改 */}
          <div>
            <h2 className="mb-6 flex items-center gap-2 text-xl font-semibold">
              <Key className="h-5 w-5 text-blue-400" />
              修改登录密码
            </h2>
            
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-white/70">
                  当前密码
                </label>
                <div className="relative">
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/30 backdrop-blur-sm focus:border-blue-400/50 focus:outline-none focus:ring-2 focus:ring-blue-400/20"
                    placeholder="请输入当前密码"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-white/70">
                  新密码
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/30 backdrop-blur-sm focus:border-blue-400/50 focus:outline-none focus:ring-2 focus:ring-blue-400/20"
                    placeholder="请输入新密码（至少6位）"
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-white/70">
                  确认新密码
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/30 backdrop-blur-sm focus:border-blue-400/50 focus:outline-none focus:ring-2 focus:ring-blue-400/20"
                    placeholder="请再次输入新密码"
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-white/70 hover:bg-white/5"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  {showPassword ? '隐藏密码' : '显示密码'}
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-blue-500 px-6 py-3 font-medium text-white transition hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"></div>
                    保存中...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    修改密码
                  </>
                )}
              </button>
            </form>
          </div>

          {/* 系统信息 */}
          <div>
            <h2 className="mb-4 text-xl font-semibold">系统信息</h2>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/70">数据库密码设置：</span>
                  <span className="text-white">
                    {settings.auth_password ? '已设置' : '使用默认密码'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/70">环境变量密码：</span>
                  <span className="text-white">已配置</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}